import type { JSX as LocalJSX } from '@familyjs/core/components';
import React, { Fragment } from 'react';

import { NavContext } from '../../contexts/NavContext';
import PageManager from '../../routing/PageManager';
import { HTMLElementSSR } from '../../utils/HTMLElementSSR';
import { FmlRouterOutlet } from '../FmlRouterOutlet';

import { FmlTabBar } from './FmlTabBar';
import type { FmlTabsContextState } from './FmlTabsContext';
import { FmlTabsContext } from './FmlTabsContext';

class FmlTabsElement extends HTMLElementSSR {
  constructor() {
    super();
  }
}

// TODO(FW-2959): types

if (typeof (window as any) !== 'undefined' && window.customElements) {
  const element = window.customElements.get('fml-tabs');
  if (!element) {
    window.customElements.define('fml-tabs', FmlTabsElement);
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'fml-tabs': any;
    }
  }
}

type ChildFunction = (fmlTabContext: FmlTabsContextState) => React.ReactNode;

interface Props extends LocalJSX.FmlTabs {
  className?: string;
  children: ChildFunction | React.ReactNode;
}

const hostStyles: React.CSSProperties = {
  display: 'flex',
  position: 'absolute',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  contain: 'layout size style',
};

const tabsInner: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  contain: 'layout size style',
};

export const FmlTabs = /*@__PURE__*/ (() =>
  class extends React.Component<Props> {
    context!: React.ContextType<typeof NavContext>;
    routerOutletRef: React.Ref<HTMLFmlRouterOutletElement> = React.createRef();
    selectTabHandler?: (tag: string) => boolean;
    tabBarRef = React.createRef<any>();

    fmlTabContextState: FmlTabsContextState = {
      activeTab: undefined,
      selectTab: () => false,
    };

    constructor(props: Props) {
      super(props);
    }

    componentDidMount() {
      if (this.tabBarRef.current) {
        // Grab initial value
        this.fmlTabContextState.activeTab = this.tabBarRef.current.state.activeTab;
        // Override method
        this.tabBarRef.current.setActiveTabOnContext = (tab: string) => {
          this.fmlTabContextState.activeTab = tab;
        };
        this.fmlTabContextState.selectTab = this.tabBarRef.current.selectTab;
      }
    }

    render() {
      let outlet: React.ReactElement<{}> | undefined;
      let tabBar: React.ReactElement | undefined;
      const { className, onFmlTabsDidChange, onFmlTabsWillChange, ...props } = this.props;

      const children =
        typeof this.props.children === 'function'
          ? (this.props.children as ChildFunction)(this.fmlTabContextState)
          : this.props.children;

      React.Children.forEach(children, (child: any) => {
        // eslint-disable-next-line no-prototype-builtins
        if (child == null || typeof child !== 'object' || !child.hasOwnProperty('type')) {
          return;
        }
        if (child.type === FmlRouterOutlet || child.type.isRouterOutlet) {
          outlet = React.cloneElement(child);
        } else if (child.type === Fragment && child.props.children[0].type === FmlRouterOutlet) {
          outlet = child.props.children[0];
        }

        let childProps: any = {
          ref: this.tabBarRef,
        };

        /**
         * Only pass these props
         * down from FmlTabs to FmlTabBar
         * if they are defined, otherwise
         * if you have a handler set on
         * FmlTabBar it will be overridden.
         */
        if (onFmlTabsDidChange !== undefined) {
          childProps = {
            ...childProps,
            onFmlTabsDidChange,
          };
        }

        if (onFmlTabsWillChange !== undefined) {
          childProps = {
            ...childProps,
            onFmlTabsWillChange,
          };
        }

        if (child.type === FmlTabBar || child.type.isTabBar) {
          tabBar = React.cloneElement(child, childProps);
        } else if (
          child.type === Fragment &&
          (child.props.children[1].type === FmlTabBar || child.props.children[1].type.isTabBar)
        ) {
          tabBar = React.cloneElement(child.props.children[1], childProps);
        }
      });

      if (!outlet) {
        throw new Error('FmlTabs must contain an FmlRouterOutlet');
      }
      if (!tabBar) {
        throw new Error('FmlTabs needs a FmlTabBar');
      }

      return (
        <FmlTabsContext.Provider value={this.fmlTabContextState}>
          {this.context.hasFamilyRouter() ? (
            <PageManager className={className ? `${className}` : ''} routeInfo={this.context.routeInfo} {...props}>
              <fml-tabs className="fml-tabs" style={hostStyles}>
                {tabBar.props.slot === 'top' ? tabBar : null}
                <div style={tabsInner} className="tabs-inner">
                  {outlet}
                </div>
                {tabBar.props.slot === 'bottom' ? tabBar : null}
              </fml-tabs>
            </PageManager>
          ) : (
            <div className={className ? `${className}` : 'fml-tabs'} {...props} style={hostStyles}>
              {tabBar.props.slot === 'top' ? tabBar : null}
              <div style={tabsInner} className="tabs-inner">
                {outlet}
              </div>
              {tabBar.props.slot === 'bottom' ? tabBar : null}
            </div>
          )}
        </FmlTabsContext.Provider>
      );
    }

    static get contextType() {
      return NavContext;
    }
  })();
